<template>
  <!-- Skip to main content for keyboard users -->
  <a href="#main-content" class="visually-hidden skip-link">Skip to main content</a>

  <div id="main-content" class="profile-dashboard" :class="userRoleClass" role="main">
    <!-- Dashboard Header -->
    <header class="dashboard-header">
      <div class="welcome-section">
        <h1 class="welcome-title">
          Welcome, <span class="user-name">{{ userInfo?.firstName || 'User' }}</span>
        </h1>
        <p class="welcome-subtitle">Manage your PALLAS Gallery {{ userInfo.role === 'both' ? 'profiles' : 'profile' }} and Account Settings.</p>
      </div>
    </header>
    
    <!-- Profile Settings Section -->
    <section class="dashboard-section">
      <div class="section-header">
        <h2 class="section-title">Profile Settings</h2>
        <!-- Action buttons for single-role users -->
        <div v-if="userInfo.role !== 'both'" class="profile-actions">
          <button @click="editProfile(userInfo.role)" class="action-btn edit-btn" :aria-label="`Edit ${userInfo.role} profile`">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            <span>Edit Profile</span>
          </button>
          <button @click="viewPublicProfile" class="action-btn view-btn" aria-label="Preview your public profile">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
              <rect x="6" y="7" width="12" height="6" rx="1" ry="1" fill="currentColor" opacity="0.3"/>
            </svg>
            <span>Preview</span>
          </button>
        </div>
      </div>

      <!-- Single Profile Layout (Artist or Scientist) -->
      <div v-if="userInfo.role !== 'both'" class="single-profile-layout">
        <div class="profile-section" :class="userRoleClass">
          
          <div class="profile-content">
            <!-- Profile Completion (Compact) -->
            <div class="status-card completion compact" role="region" aria-labelledby="completion-title">
              <div class="card-header compact-header">
                <h3 id="completion-title">Profile Completion</h3>
              </div>
              <div class="completion-status">
                <div class="completion-header">
                  <span class="completion-percentage">{{ completionPercentage }}%</span>
                  <div class="progress-bar" role="progressbar" :aria-valuenow="completionPercentage" aria-valuemin="0" aria-valuemax="100" :aria-valuetext="`Profile ${completionPercentage}% complete`">
                    <div class="progress-fill" :style="{ width: completionPercentage + '%' }"></div>
                  </div>
                </div>
                <div v-if="incompleteTasks.length > 0" class="completion-tasks compact-tasks">
                  <div v-for="task in incompleteTasks" :key="task.key" class="task-item compact">
                    <span class="task-icon">○</span>
                    <span class="task-text">{{ task.text }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Profile Settings -->
            <div class="status-card profile-settings" :class="{ 'settings-locked': !isProfileComplete }" role="region" aria-labelledby="settings-title">
              <div class="card-header">
                <h3 id="settings-title">{{ isProfileComplete ? 'Visibility Settings' : 'Complete your profile to publish' }}</h3>
              </div>
              <p v-if="!isProfileComplete" class="locked-message">Finish the tasks above to make your profile visible in the PALLAS Gallery.</p>
              <div class="profile-toggles" :class="{ 'toggles-disabled': !isProfileComplete }">
                <div class="toggle-item">
                  <span class="toggle-label" id="collab-label">Open to Collaborations</span>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="userInfo.openToCollaborations" @change="updateSetting('openToCollaborations', $event.target.checked)" :disabled="!isProfileComplete" aria-labelledby="collab-label" aria-describedby="collab-help">
                    <span class="toggle-slider"></span>
                  </label>
                  <span id="collab-help" class="visually-hidden">Toggle whether you are open to collaboration requests</span>
                </div>
                <div class="toggle-item">
                  <span class="toggle-label" id="visible-label">Profile Visible</span>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="userInfo.profileVisible" @change="updateSetting('profileVisible', $event.target.checked)" :disabled="!isProfileComplete" aria-labelledby="visible-label" aria-describedby="visible-help">
                    <span class="toggle-slider"></span>
                  </label>
                  <span id="visible-help" class="visually-hidden">Toggle whether your profile is visible to other users</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Dual Profile Layout (Both Artist and Scientist) -->
      <div v-else class="dual-profile-layout">
        <!-- Artist Profile (Left) -->
        <div class="profile-section artist-section">
          <div class="profile-header">
            <h2 class="profile-type-title artist-title">Artist Profile</h2>
            <div class="profile-actions">
              <button @click="editProfile('artist')" class="action-btn edit-btn artist-btn" aria-label="Edit artist profile">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                <span>Edit</span>
              </button>
              <button @click="viewPublicProfile('artist')" class="action-btn view-btn artist-btn" aria-label="Preview public artist profile">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                  <rect x="6" y="7" width="12" height="6" rx="1" ry="1" fill="currentColor" opacity="0.3"/>
                </svg>
                <span>Preview</span>
              </button>
            </div>
          </div>
          
          <!-- Artist Profile Completion (under header) -->
          <div class="completion-status artist-completion">
            <div class="completion-header">
              <span class="completion-percentage artist-percentage">{{ artistCompletionPercentage }}%</span>
              <div class="progress-bar">
                <div class="progress-fill artist-progress" :style="{ width: artistCompletionPercentage + '%' }"></div>
              </div>
            </div>
            <div v-if="artistIncompleteTasks.length > 0" class="completion-tasks compact-tasks">
              <span v-for="task in artistIncompleteTasks" :key="task.key" class="task-item compact">
                <span class="task-icon">○</span> {{ task.text }}
              </span>
            </div>
          </div>

          <!-- Artist Profile Settings (Toggles) -->
          <div class="profile-settings-section artist-settings" :class="{ 'settings-locked': !isArtistProfileComplete }">
            <h4 v-if="!isArtistProfileComplete" class="locked-header">Complete your profile to publish</h4>
            <p v-if="!isArtistProfileComplete" class="locked-message">Finish the tasks above to make your artist profile visible in the PALLAS Gallery.</p>
            <div class="profile-toggles" :class="{ 'toggles-disabled': !isArtistProfileComplete }">
              <div class="toggle-item">
                <span class="toggle-label" id="artist-collab-label">Open to Collaborations</span>
                <label class="toggle-switch">
                  <input type="checkbox" v-model="userInfo.artistCollaborations" @change="updateSetting('artistCollaborations', $event.target.checked)" :disabled="!isArtistProfileComplete" aria-labelledby="artist-collab-label">
                  <span class="toggle-slider artist-toggle"></span>
                </label>
              </div>
              <div class="toggle-item">
                <span class="toggle-label" id="artist-visible-label">Profile Visible</span>
                <label class="toggle-switch">
                  <input type="checkbox" v-model="userInfo.artistVisible" @change="updateSetting('artistVisible', $event.target.checked)" :disabled="!isArtistProfileComplete" aria-labelledby="artist-visible-label">
                  <span class="toggle-slider artist-toggle"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Scientist Profile (Right) -->
        <div class="profile-section scientist-section">
          <div class="profile-header">
            <h2 class="profile-type-title scientist-title">Scientist Profile</h2>
            <div class="profile-actions">
              <button @click="editProfile('scientist')" class="action-btn edit-btn scientist-btn" aria-label="Edit scientist profile">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                <span>Edit</span>
              </button>
              <button @click="viewPublicProfile('scientist')" class="action-btn view-btn scientist-btn" aria-label="Preview public scientist profile">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                  <rect x="6" y="7" width="12" height="6" rx="1" ry="1" fill="currentColor" opacity="0.3"/>
                </svg>
                <span>Preview</span>
              </button>
            </div>
          </div>

          <!-- Scientist Profile Completion (under header) -->
          <div class="completion-status scientist-completion">
            <div class="completion-header">
              <span class="completion-percentage scientist-percentage">{{ scientistCompletionPercentage }}%</span>
              <div class="progress-bar">
                <div class="progress-fill scientist-progress" :style="{ width: scientistCompletionPercentage + '%' }"></div>
              </div>
            </div>
            <div v-if="scientistIncompleteTasks.length > 0" class="completion-tasks compact-tasks">
              <span v-for="task in scientistIncompleteTasks" :key="task.key" class="task-item compact">
                <span class="task-icon">○</span> {{ task.text }}
              </span>
            </div>
          </div>

          <!-- Scientist Profile Settings (Toggles) -->
          <div class="profile-settings-section scientist-settings" :class="{ 'settings-locked': !isScientistProfileComplete }">
            <h4 v-if="!isScientistProfileComplete" class="locked-header">Complete your profile to publish</h4>
            <p v-if="!isScientistProfileComplete" class="locked-message">Finish the tasks above to make your scientist profile visible in the PALLAS Gallery.</p>
            <div class="profile-toggles" :class="{ 'toggles-disabled': !isScientistProfileComplete }">
              <div class="toggle-item">
                <span class="toggle-label" id="scientist-collab-label">Open to Collaborations</span>
                <label class="toggle-switch">
                  <input type="checkbox" v-model="userInfo.scientistCollaborations" @change="updateSetting('scientistCollaborations', $event.target.checked)" :disabled="!isScientistProfileComplete" aria-labelledby="scientist-collab-label">
                  <span class="toggle-slider scientist-toggle"></span>
                </label>
              </div>
              <div class="toggle-item">
                <span class="toggle-label" id="scientist-visible-label">Profile Visible</span>
                <label class="toggle-switch">
                  <input type="checkbox" v-model="userInfo.scientistVisible" @change="updateSetting('scientistVisible', $event.target.checked)" :disabled="!isScientistProfileComplete" aria-labelledby="scientist-visible-label">
                  <span class="toggle-slider scientist-toggle"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Account Settings Section -->
    <section class="dashboard-section account-section">
      <div class="section-header">
        <h2 class="section-title">Account Settings</h2>
      </div>

      <!-- Account Settings Grid - 4 columns -->
      <div class="account-settings-grid">
        <!-- Column 1: Account Type + Email Notifications stacked -->
        <div class="setting-card-stack">
          <!-- Account Type -->
          <div class="setting-card">
            <h4>Account Type</h4>
            <p class="setting-description-dark">Change how you're identified across PALLAS</p>
            <div class="role-slider-container-card">
              <RoleSlider v-if="userInfo.role" :modelValue="userInfo.role" @update:modelValue="confirmRoleChange" :showIndicator="false" />
              <div v-else class="role-loading">Loading...</div>
            </div>
          </div>

          <!-- Email Notifications (removed - no backend pipeline yet) -->
        </div>

        <!-- Column 2: Personal Information -->
        <div class="setting-card">
          <h4>Personal Information</h4>
          <div class="setting-fields">
            <div class="field-group">
              <label for="first-name-input">First Name <span class="required" aria-label="required">*</span></label>
              <input type="text" id="first-name-input" v-model="tempUserInfo.firstName" class="setting-input" required autocomplete="given-name" />
            </div>
            <div class="field-group">
              <label for="last-name-input">Last Name <span class="required" aria-label="required">*</span></label>
              <input type="text" id="last-name-input" v-model="tempUserInfo.lastName" class="setting-input" required autocomplete="family-name" />
            </div>
            <div class="field-group">
              <label for="email-input">Email <span class="required" aria-label="required">*</span></label>
              <input type="email" id="email-input" v-model="tempUserInfo.email" class="setting-input" required autocomplete="email" />
            </div>
            <button @click="updatePersonalInfo" class="save-btn" :disabled="!personalInfoChanged">Save Changes</button>
          </div>
        </div>

        <!-- Column 4: Password -->
        <div class="setting-card">
          <h4>Password</h4>
          <div class="setting-fields">
            <div class="field-group">
              <label for="current-password">Current Password <span class="required" aria-label="required">*</span></label>
              <input type="password" id="current-password" v-model="passwordFields.current" class="setting-input" required autocomplete="current-password" />
            </div>
            <div class="field-group">
              <label for="new-password">New Password <span class="required" aria-label="required">*</span></label>
              <input type="password" id="new-password" v-model="passwordFields.new" class="setting-input" required autocomplete="new-password" minlength="8" />
              <span class="field-help">Min 8 characters</span>
            </div>
            <div class="field-group">
              <label for="confirm-password">Confirm Password <span class="required" aria-label="required">*</span></label>
              <input type="password" id="confirm-password" v-model="passwordFields.confirm" class="setting-input" required autocomplete="new-password" />
            </div>
            <button @click="updatePassword" class="save-btn" :disabled="!passwordFields.current || !passwordFields.new">Update Password</button>
          </div>
        </div>

        <!-- Column 4: Delete Account -->
        <div class="setting-card delete-card">
          <h4>Delete Account</h4>
          <p class="setting-description-dark">Permanently remove your account and all data</p>
          <button class="delete-account-btn" @click="showDeleteConfirmation = true" aria-describedby="delete-warning">
            <svg viewBox="0 0 448 512" width="12" height="12" fill="currentColor" aria-hidden="true">
              <path d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z"></path>
            </svg>
            Delete Account
          </button>
          <span id="delete-warning" class="visually-hidden">Warning: This action will permanently delete your account and cannot be undone</span>
          <p class="support-link">Don't see something? <a href="mailto:support@pallasart.com">Contact support here</a></p>
        </div>
      </div>
    </section>

    <!-- Role Change Confirmation Modal -->
    <div v-if="showRoleConfirmation" class="modal-overlay" @click.self="cancelRoleChange" role="dialog" aria-modal="true" aria-labelledby="role-modal-title">
      <div class="confirmation-modal">
        <h3 id="role-modal-title">Confirm Account Type Change</h3>
        <p>Are you sure you want to change your account type to <strong>{{ pendingRole }}</strong>?</p>
        <p class="confirmation-note">This will update how you're identified across PALLAS and may affect your profile visibility.</p>
        <div class="confirmation-actions">
          <button @click="cancelRoleChange" class="cancel-btn" aria-label="Cancel account type change">Cancel</button>
          <button @click="updateRole" class="confirm-btn" :class="roleColorClass" :aria-label="`Confirm change to ${pendingRole} account type`">Confirm Change</button>
        </div>
      </div>
    </div>

    <!-- Delete Account Confirmation Modal -->
    <div v-if="showDeleteConfirmation" class="modal-overlay" @click.self="cancelAccountDeletion" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
      <div class="confirmation-modal delete-modal">
        <h3 id="delete-modal-title">Confirm Account Deletion</h3>
        <div class="warning-icon">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="#dc3545" aria-hidden="true">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h2v2h-2v-2zm0-8h2v6h-2V9z"/>
          </svg>
        </div>
        <p>Are you sure you want to <strong>permanently delete</strong> your PALLAS account?</p>
        <p class="confirmation-note warning-text">This action cannot be undone. All your profile data, portfolio items, and account history will be permanently removed.</p>
        <div class="confirmation-actions">
          <button @click="cancelAccountDeletion" class="cancel-btn" aria-label="Cancel account deletion">Cancel</button>
          <button @click="confirmAccountDeletion" class="delete-confirm-btn" aria-label="Permanently delete my account">Delete My Account</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updateEmail, updatePassword as updateFirebasePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import RoleSlider from './RoleSlider.vue';

export default {
  name: 'ProfileDashboard',
  components: {
    RoleSlider
  },
  setup() {
    const router = useRouter();
    const userInfo = reactive({
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      bio: '',
      researchBio: '',
      portfolio: [],
      analytics: {
        profileViews: 0,
        contactClicks: 0,
        portfolioViews: 0
      },
      verified: false,
      profilePictureURL: '',
      profileBackgroundURL: '',
      tagsSelected: [],
      contactInfo: null,
      openToCollaborations: true,
      profileVisible: true,
      collaborationCount: 0,
      artistCollaborations: true,
      scientistCollaborations: true,
      artistVisible: true,
      scientistVisible: true
    });
    
    const tempUserInfo = reactive({
      firstName: '',
      lastName: '',
      email: ''
    });
    
    const passwordFields = reactive({
      current: '',
      new: '',
      confirm: ''
    });
    
    const showRoleConfirmation = ref(false);
    const pendingRole = ref('');
    const showDeleteConfirmation = ref(false);
    
    const personalInfoChanged = computed(() => {
      return tempUserInfo.firstName !== userInfo.firstName ||
             tempUserInfo.lastName !== userInfo.lastName ||
             tempUserInfo.email !== userInfo.email;
    });
    
    const activeTab = ref('overview');


    const completionPercentage = computed(() => {
      let completed = 0;
      let total = 5; // Base required items for all roles

      // Required items (same for all roles)
      if (userInfo.firstName && userInfo.lastName) completed++;
      if (userInfo.bio && userInfo.bio.length > 20) completed++;
      if (userInfo.profilePictureURL) completed++;
      if (userInfo.profileBackgroundURL) completed++;
      if (userInfo.tagsSelected && userInfo.tagsSelected.length > 0) completed++;

      // Role-specific requirements
      if (userInfo.role === 'scientist') {
        total++; // Publications required for scientists
        if (userInfo.publications && userInfo.publications.trim()) completed++;
      } else if (userInfo.role === 'artist') {
        total++; // Portfolio required for artists
        if (userInfo.portfolio && userInfo.portfolio.length > 0) completed++;
      } else if (userInfo.role === 'both') {
        total += 2; // Both publications AND portfolio required
        if (userInfo.publications && userInfo.publications.trim()) completed++;
        if (userInfo.portfolio && userInfo.portfolio.length > 0) completed++;
      }

      return Math.round((completed / total) * 100);
    });

    const isProfileComplete = computed(() => {
      return completionPercentage.value === 100;
    });

    // For single-profile layout (artist OR scientist role)
    const incompleteTasks = computed(() => {
      const tasks = [];

      // Universal required tasks
      if (!userInfo.firstName || !userInfo.lastName) tasks.push({ key: 'name', text: 'Add your full name' });
      if (!userInfo.bio || userInfo.bio.length <= 20) tasks.push({ key: 'bio', text: 'Write your bio' });
      if (!userInfo.profilePictureURL) tasks.push({ key: 'photo', text: 'Add profile photo' });
      if (!userInfo.profileBackgroundURL) tasks.push({ key: 'background', text: 'Add background photo' });
      if (!userInfo.tagsSelected || userInfo.tagsSelected.length === 0) tasks.push({ key: 'tags', text: 'Select areas of expertise/interest' });

      // Role-specific requirements
      if (userInfo.role === 'scientist') {
        if (!userInfo.publications || !userInfo.publications.trim()) tasks.push({ key: 'publications', text: 'Add publications & research' });
      } else if (userInfo.role === 'artist') {
        if (!userInfo.portfolio || userInfo.portfolio.length === 0) tasks.push({ key: 'portfolio', text: 'Add portfolio item' });
      }

      return tasks;
    });

    // For dual-profile layout - Artist section (role='both')
    const artistIncompleteTasks = computed(() => {
      const tasks = [];

      // Required tasks for artist profile
      if (!userInfo.firstName || !userInfo.lastName) tasks.push({ key: 'name', text: 'Add your full name' });
      if (!userInfo.bio || userInfo.bio.length <= 20) tasks.push({ key: 'bio', text: 'Write your bio' });
      if (!userInfo.profilePictureURL) tasks.push({ key: 'photo', text: 'Add profile photo' });
      if (!userInfo.profileBackgroundURL) tasks.push({ key: 'background', text: 'Add background photo' });
      if (!userInfo.tagsSelected || userInfo.tagsSelected.length === 0) tasks.push({ key: 'tags', text: 'Select areas of expertise/interest' });
      if (!userInfo.portfolio || userInfo.portfolio.length === 0) tasks.push({ key: 'portfolio', text: 'Add portfolio item' });

      return tasks;
    });

    // For dual-profile layout - Scientist section (role='both')
    const scientistIncompleteTasks = computed(() => {
      const tasks = [];

      // Required tasks for scientist profile
      if (!userInfo.firstName || !userInfo.lastName) tasks.push({ key: 'name', text: 'Add your full name' });
      if (!userInfo.bio || userInfo.bio.length <= 20) tasks.push({ key: 'bio', text: 'Write your bio' });
      if (!userInfo.profilePictureURL) tasks.push({ key: 'photo', text: 'Add profile photo' });
      if (!userInfo.profileBackgroundURL) tasks.push({ key: 'background', text: 'Add background photo' });
      if (!userInfo.tagsSelected || userInfo.tagsSelected.length === 0) tasks.push({ key: 'tags', text: 'Select areas of expertise/interest' });
      if (!userInfo.publications || !userInfo.publications.trim()) tasks.push({ key: 'publications', text: 'Add publications & research' });

      return tasks;
    });

    // Profile completion checks for dual-profile layout
    const artistCompletionPercentage = computed(() => {
      let completed = 0;
      const total = 6; // Base 5 + portfolio for artists

      if (userInfo.firstName && userInfo.lastName) completed++;
      if (userInfo.bio && userInfo.bio.length > 20) completed++;
      if (userInfo.profilePictureURL) completed++;
      if (userInfo.profileBackgroundURL) completed++;
      if (userInfo.tagsSelected && userInfo.tagsSelected.length > 0) completed++;
      if (userInfo.portfolio && userInfo.portfolio.length > 0) completed++;

      return Math.round((completed / total) * 100);
    });

    const scientistCompletionPercentage = computed(() => {
      let completed = 0;
      const total = 6; // Base 5 + publications for scientists

      if (userInfo.firstName && userInfo.lastName) completed++;
      if (userInfo.bio && userInfo.bio.length > 20) completed++;
      if (userInfo.profilePictureURL) completed++;
      if (userInfo.profileBackgroundURL) completed++;
      if (userInfo.tagsSelected && userInfo.tagsSelected.length > 0) completed++;
      if (userInfo.publications && userInfo.publications.trim()) completed++;

      return Math.round((completed / total) * 100);
    });

    const isArtistProfileComplete = computed(() => {
      return artistIncompleteTasks.value.length === 0;
    });

    const isScientistProfileComplete = computed(() => {
      return scientistIncompleteTasks.value.length === 0;
    });

    const roleColorClass = computed(() => {
      switch (pendingRole.value) {
        case 'scientist': return 'confirm-scientist';
        case 'artist': return 'confirm-artist';
        case 'both': return 'confirm-both';
        default: return '';
      }
    });

    const userRoleColor = computed(() => {
      switch (userInfo.role) {
        case 'scientist': return '#6566be';
        case 'artist': return '#f8b19b';
        case 'both': return '#AF8CAD';
        default: return '#6566be';
      }
    });

    const userRoleClass = computed(() => {
      switch (userInfo.role) {
        case 'scientist': return 'role-scientist';
        case 'artist': return 'role-artist';
        case 'both': return 'role-both';
        default: return 'role-scientist';
      }
    });

    const loadUserData = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            Object.assign(userInfo, {
              ...userData,
              portfolio: userData.portfolio || [],
              tagsSelected: userData.tagsSelected || [],
              openToCollaborations: userData.openToCollaborations !== undefined ? userData.openToCollaborations : true,
              profileVisible: userData.profileVisible !== undefined ? userData.profileVisible : true,
              artistVisible: userData.artistVisible !== undefined ? userData.artistVisible : true,
              scientistVisible: userData.scientistVisible !== undefined ? userData.scientistVisible : true,
            });
            
            
            // Initialize temp fields
            tempUserInfo.firstName = userInfo.firstName;
            tempUserInfo.lastName = userInfo.lastName;
            tempUserInfo.email = userInfo.email;
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };
    
    const updatePersonalInfo = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          // Update Firestore
          await updateDoc(doc(db, 'users', user.uid), {
            firstName: tempUserInfo.firstName,
            lastName: tempUserInfo.lastName,
            lastUpdated: new Date()
          });
          
          // Update email in Auth if changed
          if (tempUserInfo.email !== userInfo.email) {
            await updateEmail(user, tempUserInfo.email);
          }
          
          // Update local state
          userInfo.firstName = tempUserInfo.firstName;
          userInfo.lastName = tempUserInfo.lastName;
          userInfo.email = tempUserInfo.email;
          
          alert('Personal information updated successfully');
        }
      } catch (error) {
        console.error('Error updating personal info:', error);
        alert('Error updating information. Please try again.');
      }
    };
    
    const updatePasswordFunc = async () => {
      if (passwordFields.new !== passwordFields.confirm) {
        alert('New passwords do not match');
        return;
      }
      
      try {
        const user = auth.currentUser;
        if (user) {
          // Re-authenticate user
          const credential = EmailAuthProvider.credential(
            user.email,
            passwordFields.current
          );
          await reauthenticateWithCredential(user, credential);
          
          // Update password
          await updateFirebasePassword(user, passwordFields.new);
          
          // Clear fields
          passwordFields.current = '';
          passwordFields.new = '';
          passwordFields.confirm = '';
          
          alert('Password updated successfully');
        }
      } catch (error) {
        console.error('Error updating password:', error);
        alert('Error updating password. Please check your current password.');
      }
    };

    const updateSetting = async (setting, value) => {
      try {
        const user = auth.currentUser;
        if (user) {
          await updateDoc(doc(db, 'users', user.uid), {
            [setting]: value,
            lastUpdated: new Date()
          });
        }
      } catch (error) {
        console.error('Error updating setting:', error);
      }
    };
    
    const confirmRoleChange = (newRole) => {
      if (newRole !== userInfo.role) {
        pendingRole.value = newRole;
        showRoleConfirmation.value = true;
      }
    };
    
    const updateRole = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          await updateDoc(doc(db, 'users', user.uid), {
            role: pendingRole.value,
            lastUpdated: new Date()
          });
          userInfo.role = pendingRole.value;
          showRoleConfirmation.value = false;
          alert(`Account type updated to ${pendingRole.value}. Your profile has been updated across PALLAS.`);
        }
      } catch (error) {
        console.error('Error updating role:', error);
        alert('Error updating account type. Please try again.');
      }
    };
    
    const cancelRoleChange = () => {
      showRoleConfirmation.value = false;
      // Force re-render of role slider to original value
      const temp = userInfo.role;
      userInfo.role = '';
      setTimeout(() => {
        userInfo.role = temp;
      }, 10);
    };

    const editProfile = (profileType) => {
      router.push(`/profile-edit?type=${profileType}`);
    };

    const viewPublicProfile = (viewType = null) => {
      const user = auth.currentUser;
      if (user) {
        // For "both" users, add view param to show correct profile
        if (viewType) {
          router.push(`/profile-new/${user.uid}?view=${viewType}`);
        } else {
          router.push(`/profile-new/${user.uid}`);
        }
      }
    };


    const cancelAccountDeletion = () => {
      showDeleteConfirmation.value = false;
    };

    const confirmAccountDeletion = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          // Delete user data from Firestore
          await updateDoc(doc(db, 'users', user.uid), {
            deleted: true,
            deletedAt: new Date(),
            email: `deleted_${Date.now()}@deleted.com`,
            firstName: 'Deleted',
            lastName: 'User'
          });
          
          // Delete the user account
          await user.delete();
          
          // Redirect to home page
          router.push('/');
          alert('Your account has been permanently deleted.');
        }
      } catch (error) {
        console.error('Error deleting account:', error);
        alert('Error deleting account. Please try again or contact support.');
      }
    };


    onMounted(() => {
      loadUserData();
    });

    return {
      userInfo,
      completionPercentage,
      isProfileComplete,
      incompleteTasks,
      artistIncompleteTasks,
      scientistIncompleteTasks,
      artistCompletionPercentage,
      scientistCompletionPercentage,
      isArtistProfileComplete,
      isScientistProfileComplete,
      editProfile,
      viewPublicProfile,
      updateSetting,
      updateRole,
      tempUserInfo,
      passwordFields,
      personalInfoChanged,
      updatePersonalInfo,
      updatePassword: updatePasswordFunc,
      activeTab,
      showRoleConfirmation,
      pendingRole,
      confirmRoleChange,
      cancelRoleChange,
      roleColorClass,
      showDeleteConfirmation,
      cancelAccountDeletion,
      confirmAccountDeletion,
      userRoleColor,
      userRoleClass
    };
  }
};
</script>

<style scoped>
/* ===== ACCESSIBILITY STYLES ===== */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background: var(--sciColor);
  color: white;
  padding: 8px;
  text-decoration: none;
  border-radius: 4px;
  z-index: 10000;
  font-weight: 600;
  transition: top 0.2s ease;
}

.skip-link:focus {
  top: 6px;
}

.required {
  color: #dc3545;
  font-weight: bold;
  margin-left: 2px;
}

.field-help {
  font-size: var(--labelText);
  color: #6c757d;
  margin-top: 4px;
  display: block;
}

/* Remove outlines for mouse users but keep for keyboard users */
.action-btn:focus:not(:focus-visible),
.save-btn:focus:not(:focus-visible),
.cancel-btn:focus:not(:focus-visible),
.confirm-btn:focus:not(:focus-visible),
.delete-confirm-btn:focus:not(:focus-visible),
.delete-account-btn:focus:not(:focus-visible) {
  outline: none;
}

/* Enhanced focus indicators for keyboard users */
.action-btn:focus-visible,
.save-btn:focus-visible,
.cancel-btn:focus-visible,
.confirm-btn:focus-visible,
.delete-confirm-btn:focus-visible,
.delete-account-btn:focus-visible,
.setting-input:focus-visible,
.toggle-switch input:focus-visible + .toggle-slider {
  outline: 2px solid var(--sciColor);
  outline-offset: 2px;
}

.role-artist .action-btn:focus-visible,
.role-artist .save-btn:focus-visible,
.role-artist .setting-input:focus-visible,
.role-artist .toggle-switch input:focus-visible + .toggle-slider {
  outline-color: #f8b19b;
}

.role-both .action-btn:focus-visible,
.role-both .save-btn:focus-visible,
.role-both .setting-input:focus-visible,
.role-both .toggle-switch input:focus-visible + .toggle-slider {
  outline-color: #AF8CAD;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .status-card {
    border-color: #000;
    border-width: 2px;
  }

  .progress-bar {
    border: 1px solid #000;
  }

  .toggle-slider {
    border: 1px solid #000;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .tab-content,
  .action-btn,
  .save-btn,
  .toggle-slider,
  .progress-fill,
  .modal-overlay {
    animation: none !important;
    transition: none !important;
  }
}

/* Touch target size for mobile */
@media (max-width: var(--breakpointTablet)) {
  .action-btn,
  .save-btn,
  .cancel-btn,
  .confirm-btn,
  .delete-confirm-btn,
  .delete-account-btn,
  .tab-radio .tab-name {
    min-height: 44px;
    min-width: 44px;
    padding: 12px 16px;
  }

  .toggle-switch {
    min-height: 44px;
    min-width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .toggle-switch input {
    min-height: 44px;
    min-width: 44px;
  }
}

/* ===== MAIN CONTAINER ===== */
.profile-dashboard {
  width: 100%;
  min-height: 100vh;
  padding: calc(var(--navbarHeight) + 2rem) 1rem 2rem;
  background: transparent;
  max-width: 1400px;
  margin: 0 auto;
}

/* ===== DASHBOARD HEADER ===== */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 3rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.welcome-title {
  font-size: 2.5rem;
  font-weight: var(--font-weight-bold);
  color: var(--headersColor);
  margin: 0 0 0.5rem 0;
  font-family: var(--font-primary);
}

.user-name {
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.welcome-subtitle {
  font-size: var(--buttonText);
  color: var(--textColor);
  opacity: 0.8;
  margin: 0;
}

.quick-actions {
  display: flex;
  gap: var(--paddingStandard);
}

.action-btn {
  display: flex;
  align-items: center;
  gap: var(--paddingTight);
  padding: var(--paddingTight) var(--paddingStandard);
  border: none;
  border-radius: var(--stdRadius);
  font-size: var(--buttonText);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--stdTransition);
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.action-btn::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  border-radius: var(--stdRadius);
  z-index: -1;
  opacity: 0;
  transition: opacity var(--stdTransition);
}

.action-btn:hover {
  opacity: 0.8;
  transform: none;
}

/* ===== STATUS GRID ===== */
.status-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: var(--paddingSpaceous);
  margin-bottom: 3rem;
}

.status-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all var(--stdTransition);
}


.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--paddingStandard);
}

.card-header h3 {
  font-size: var(--buttonText);
  font-weight: var(--font-weight-bold);
  color: var(--headersColor);
  margin: 0;
}

.completion-percentage {
  font-size: 2rem;
  font-weight: var(--font-weight-bold);
  color: var(--sciColor);
}

.role-scientist .completion-percentage {
  color: #6566be !important;
}

.role-artist .completion-percentage {
  color: #f8b19b !important;
}

.role-both .completion-percentage {
  color: #AF8CAD !important;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #d8d7e2;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: var(--paddingStandard);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  border-radius: 4px;
  transition: width 0.5s ease;
}

/* Compact completion card for single-role layout */
.status-card.completion.compact {
  padding: var(--paddingStandard);
}

.status-card.completion.compact .card-header.compact-header {
  margin-bottom: var(--paddingTight);
}

.status-card.completion.compact .card-header.compact-header h3 {
  font-size: 0.85rem;
}

.status-card.completion.compact .completion-status {
  border-top: none;
  margin-top: 0;
  padding-top: 0;
}

/* Compact completion status layout */
.completion-status {
  padding: var(--paddingTight) 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: var(--paddingTight);
}

.completion-header {
  display: flex;
  align-items: center;
  gap: var(--paddingStandard);
  margin-bottom: var(--paddingTight);
}

.completion-status .completion-percentage {
  font-size: 1.1rem;
  font-weight: 600;
  min-width: 45px;
}

.completion-status .progress-bar {
  flex: 1;
  height: 6px;
  margin-bottom: 0;
}

.completion-tasks.compact-tasks {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: wrap;
  gap: 2px 10px;
}

.task-item.compact {
  font-size: 0.7rem;
  color: var(--textColor);
  opacity: 0.7;
  display: inline-flex !important;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
}

.task-item.compact .task-icon {
  font-size: 0.55rem;
}

/* Legacy styles for non-compact layout */
.completion-tasks {
  display: flex;
  flex-direction: column;
  gap: var(--paddingTight);
}

.task-item {
  display: flex;
  align-items: center;
  gap: var(--paddingTight);
  font-size: var(--labelText);
  color: var(--textColor);
}

.task-icon {
  color: var(--artColor);
  font-weight: bold;
}

/* Dashboard sections */
.dashboard-section {
  margin-bottom: var(--paddingSpaceous);
}

.dashboard-section.account-section {
  margin-top: var(--paddingSpaceous);
  padding-top: var(--paddingSpaceous);
}

/* Profile settings toggles section */
.profile-settings-section {
  padding: var(--paddingStandard) 0;
}

.profile-settings-section .profile-toggles {
  display: flex;
  flex-direction: column;
  gap: var(--paddingTight);
}

/* Locked/disabled settings state */
.settings-locked {
  opacity: 0.85;
}

.locked-header {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--headersColor);
  margin: 0 0 4px 0;
}

.locked-message {
  font-size: 0.7rem;
  color: #666;
  margin: 0 0 var(--paddingTight) 0;
  line-height: 1.4;
}

.toggles-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.toggles-disabled .toggle-slider {
  cursor: not-allowed;
}

/* Section-specific colors for dual profile layout - these take precedence */
.dual-profile-layout .scientist-section .task-icon {
  color: #6566be !important;
}

.dual-profile-layout .artist-section .task-icon {
  color: #f8b19b !important;
}

/* Role-based colors for single profile layout */
.role-scientist .single-profile-layout .task-icon {
  color: #6566be !important;
}

.role-artist .single-profile-layout .task-icon {
  color: #f8b19b !important;
}

.role-both .single-profile-layout .task-icon {
  color: #AF8CAD !important;
}

/* ===== ANALYTICS SECTION ===== */
.analytics-section,
.settings-section {
  margin-bottom: 3rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--paddingSpaceous);
  padding-left: var(--paddingSpaceous);
}

.section-title {
  font-size: var(--subTitle);
  font-weight: var(--font-weight-bold);
  color: var(--headersColor);
  margin: 0;
  padding-left: 0;
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--paddingSpaceous);
}

.analytics-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: var(--paddingStandard);
  transition: all var(--stdTransition);
}

.analytics-card:hover {
  transform: translateY(-2px);
}

.analytics-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.analytics-card.views .analytics-icon {
  background: rgba(101, 103, 190, 0.1);
  color: var(--sciColor);
}

.analytics-card.contacts .analytics-icon {
  background: rgba(248, 177, 155, 0.1);
  color: var(--artColor);
}

.analytics-card.portfolio .analytics-icon {
  background: rgba(209, 210, 230, 0.1);
  color: var(--blendColor);
}

.analytics-card.collaborations .analytics-icon {
  background: rgba(16, 185, 129, 0.1);
  color: #10B981;
}

.analytics-content {
  flex: 1;
}

.analytics-number {
  font-size: 1.5rem;
  font-weight: var(--font-weight-bold);
  color: var(--headersColor);
  margin-bottom: 2px;
}

.analytics-label {
  font-size: var(--labelText);
  color: var(--textColor);
  margin-bottom: 4px;
}

.analytics-change {
  font-size: var(--labelText);
  font-weight: 600;
}

.analytics-change.positive {
  color: #10B981;
}

.analytics-change.negative {
  color: #EF4444;
}

.analytics-change.neutral {
  color: var(--textColor);
  opacity: 0.6;
}

/* ===== TAB NAVIGATION ===== */
.tab-navigation {
  display: flex;
  flex-wrap: wrap;
  border-radius: var(--stdRadius);
  background-color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 0 0px 1px rgba(0, 0, 0, 0.06);
  padding: 0.25rem;
  max-width: 600px;
  margin: 0 auto 2rem;
  font-size: var(--buttonText);
}

.tab-radio {
  flex: 1 1 auto;
  text-align: center;
}

.tab-radio input {
  display: none;
}

.tab-radio .tab-name {
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: var(--stdRadius);
  border: none;
  padding: 0.75rem 0;
  color: var(--textColor);
  transition: all 0.15s ease-in-out;
  font-weight: 500;
}

.tab-radio input:checked + .tab-name {
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.tab-radio:hover .tab-name:not(.tab-radio input:checked + .tab-name) {
  background: rgba(246, 245, 250, 0.5);
}

/* ===== OVERVIEW ACTIONS ===== */
.overview-actions {
  display: flex;
  justify-content: center;
  gap: var(--paddingStandard);
  margin-bottom: 2rem;
}

/* ===== TAB CONTENT ===== */
.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== ROLE SETTING CARD ===== */
.role-setting-compact {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--paddingStandard);
  padding: var(--paddingStandard) 0;
  margin-bottom: var(--paddingStandard);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.role-setting-info {
  flex-shrink: 0;
}

.role-setting-compact .role-slider-container {
  flex: 1;
  max-width: 320px;
}

.role-setting-title {
  font-size: var(--buttonText);
  font-weight: 600;
  color: var(--headersColor);
  margin: 0;
}

.role-setting-description {
  font-size: 0.75rem;
  color: var(--textColor);
  opacity: 0.7;
  margin: 2px 0 0 0;
}


/* ===== SETTINGS SECTION ===== */
.settings-section {
  margin-bottom: 0;
}

/* Account Settings Grid - 4 columns */
.account-settings-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--paddingStandard);
}

/* Stacked cards in single column */
.setting-card-stack {
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard);
}

/* Make setting cards consistent */
.setting-card h4 {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--headersColor);
  margin: 0 0 var(--paddingStandard) 0;
}

/* Darker description text */
.setting-description-dark {
  font-size: 0.75rem;
  color: #555;
  margin: 0 0 var(--paddingStandard) 0;
  line-height: 1.4;
}

/* Darker notification items */
.notification-item-dark {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #444;
  cursor: pointer;
  font-weight: 500;
}

.notification-item-dark input[type="checkbox"] {
  width: 14px;
  height: 14px;
  margin: 0;
}

.role-scientist .notification-item-dark input[type="checkbox"] {
  accent-color: #6566be;
}

.role-artist .notification-item-dark input[type="checkbox"] {
  accent-color: #f8b19b;
}

.role-both .notification-item-dark input[type="checkbox"] {
  accent-color: #AF8CAD;
}

/* Role slider in card */
.role-slider-container-card {
  transform: scale(0.85);
  transform-origin: left center;
  margin-top: var(--paddingTight);
}

.role-loading {
  color: var(--textColor);
  opacity: 0.5;
  font-size: 0.9rem;
  padding: var(--paddingTight) 0;
}

/* Delete card styling */
.delete-card {
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.delete-card h4 {
  color: var(--headersColor);
}

/* Support link */
.support-link {
  margin-top: var(--paddingSpaceous);
  font-size: 0.7rem;
  color: #666;
}

.support-link a {
  color: var(--sciColor);
  text-decoration: underline;
}

.support-link a:hover {
  color: var(--artColor);
}

.setting-header-compact {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.setting-header-compact h4 {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--headersColor);
  margin: 0;
}

.notification-grid-compact {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 4px;
}

.notification-grid-compact.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.notification-item-small {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  color: var(--textColor);
  cursor: pointer;
}

.notification-item-small input[type="checkbox"] {
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: var(--sciColor);
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--paddingSpaceous);
}

.setting-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--stdRadius);
  padding: var(--paddingStandard);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.setting-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--paddingTight);
}

.setting-header h4 {
  font-size: var(--buttonText);
  font-weight: 600;
  color: var(--headersColor);
  margin: 0;
}

.setting-description {
  font-size: var(--labelText);
  color: var(--textColor);
  opacity: 0.8;
  margin: 0;
}

/* ===== TOGGLE SWITCH ===== */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e0e0e0;
  border: 1px solid #ccc;
  transition: var(--stdTransition);
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 2px;
  top: 50%;
  transform: translateY(-50%);
  background-color: #b8b8b8;
  transition: var(--stdTransition);
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  border-color: transparent;
}

input:checked + .toggle-slider:before {
  transform: translateX(20px) translateY(-50%);
  background-color: white;
}

.empty-state {
  text-align: center;
  padding: var(--paddingStandard);
  color: var(--textColor);
  opacity: 0.6;
}

/* ===== SETTING FIELDS ===== */
.setting-fields {
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard);
  margin-top: var(--paddingStandard);
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: var(--paddingTight);
}

.field-group label {
  font-size: var(--labelText);
  font-weight: 600;
  color: var(--headersColor);
}

.setting-input {
  padding: var(--paddingTight) var(--paddingStandard);
  border: 1px solid var(--borderColor);
  border-radius: var(--stdRadius);
  font-size: var(--labelText);
  background: white;
  color: var(--textColor);
  transition: border-color var(--stdTransition);
}

.setting-input:focus {
  outline: none;
  border-color: var(--sciColor);
}

.role-scientist .setting-input:focus {
  border-color: #6566be !important;
}

.role-artist .setting-input:focus {
  border-color: #f8b19b !important;
}

.role-both .setting-input:focus {
  border-color: #AF8CAD !important;
}

.save-btn {
  padding: var(--paddingTight) var(--paddingStandard);
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  color: white;
  border: none;
  border-radius: var(--stdRadius);
  font-size: var(--buttonText);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--stdTransition);
  margin-top: var(--paddingTight);
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== ROLE CONFIRMATION MODAL ===== */
.confirmation-modal {
  background: white;
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.confirmation-modal h3 {
  color: var(--headersColor);
  margin-bottom: var(--paddingStandard);
  font-size: var(--buttonText);
}

.confirmation-modal p {
  color: var(--textColor);
  margin-bottom: var(--paddingStandard);
  line-height: 1.4;
}

.confirmation-note {
  font-size: var(--labelText);
  opacity: 0.8;
  font-style: italic;
}

.confirmation-actions {
  display: flex;
  gap: var(--paddingStandard);
  justify-content: center;
  margin-top: var(--paddingSpaceous);
}

.cancel-btn, .confirm-btn {
  padding: var(--paddingTight) var(--paddingStandard);
  border: none;
  border-radius: var(--stdRadius);
  font-size: var(--buttonText);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--stdTransition);
}

.cancel-btn {
  background: var(--neutralColor);
  color: var(--textColor);
}

.cancel-btn:hover {
  background: rgba(209, 210, 230, 0.8);
}

.confirm-btn {
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  color: white;
}

.confirm-btn:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.confirm-btn.confirm-scientist {
  background: #6566be;
}

.confirm-btn.confirm-artist {
  background: #f8b19b;
}

.confirm-btn.confirm-both {
  background: #d1d2e6;
  color: var(--textColor);
}


.danger-btn {
  padding: var(--paddingStandard) var(--paddingSpaceous);
  border: 2px solid #c0392b;
  border-radius: var(--stdRadius);
  background: transparent;
  color: #c0392b;
  font-size: var(--buttonText);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--stdTransition);
  min-width: 200px;
}

.danger-btn:hover {
  background: #c0392b;
  color: white;
}

.delete-account-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: var(--stdRadius);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
}

.delete-account-btn:hover {
  background: #c82333;
}

.delete-account-btn svg {
  flex-shrink: 0;
}

/* Role-based dynamic coloring */
.role-scientist .save-btn,
.role-scientist .simple-form-button,
.role-scientist .action-btn,
.role-scientist input:checked + .toggle-slider,
.role-scientist .notification-item input[type="checkbox"]:checked {
  background: #6566be !important;
  accent-color: #6566be !important;
  color: white !important;
}

.role-scientist .progress-fill {
  background: #6566be !important;
}

.role-scientist .action-btn::before {
  background: #6566be !important;
}

.role-artist .save-btn,
.role-artist .simple-form-button,
.role-artist .action-btn,
.role-artist input:checked + .toggle-slider,
.role-artist .notification-item input[type="checkbox"]:checked {
  background: #f8b19b !important;
  accent-color: #f8b19b !important;
  color: white !important;
}

.role-artist .progress-fill {
  background: #f8b19b !important;
}

.role-artist .action-btn::before {
  background: #f8b19b !important;
}

.role-both .save-btn,
.role-both .simple-form-button,
.role-both .action-btn,
.role-both input:checked + .toggle-slider,
.role-both .notification-item input[type="checkbox"]:checked {
  background: #AF8CAD !important;
  accent-color: #AF8CAD !important;
  color: white !important;
}

.role-both .progress-fill {
  background: #AF8CAD !important;
}

.role-both .action-btn::before {
  background: #AF8CAD !important;
}

.notification-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--paddingTight);
  margin-top: var(--paddingStandard);
  padding: var(--paddingStandard);
  background: #f8f9fa;
  border-radius: var(--stdRadius);
  border: 1px solid var(--borderColor);
  transition: opacity 0.3s ease;
}

.notification-grid.disabled {
  opacity: 0.5;
}

.notification-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: var(--labelText);
  color: var(--textColor);
  user-select: none;
}

.notification-item input[type="checkbox"] {
  margin-right: var(--paddingTight);
  accent-color: var(--sciColor);
  transform: scale(1.1);
}

.notification-item input[type="checkbox"]:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.notification-item:has(input[type="checkbox"]:disabled) {
  cursor: not-allowed;
}

/* ===== DELETE MODAL SPECIFIC STYLING ===== */
.delete-modal {
  border: 2px solid #dc3545;
}

.warning-icon {
  display: flex;
  justify-content: center;
  margin-bottom: var(--paddingStandard);
}

.warning-text {
  color: #dc3545;
  font-weight: 600;
}

.delete-confirm-btn {
  padding: var(--paddingTight) var(--paddingStandard);
  background: #dc3545;
  color: white;
  border: none;
  border-radius: var(--stdRadius);
  font-size: var(--buttonText);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--stdTransition);
}

.delete-confirm-btn:hover {
  background: #c82333;
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

/* ===== ROLE-AWARE PROFILE SECTIONS ===== */
.single-profile-layout {
  width: 100%;
}

.dual-profile-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--paddingSpaceous);
  width: 100%;
}

.profile-section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.artist-section {
  /* Removed colored border for clean white/transparent look */
}

.scientist-section {
  /* Removed colored border for clean white/transparent look */
}

.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--paddingSpaceous);
  padding-bottom: var(--paddingStandard);
  border-bottom: 1px solid #F6F5FA;
}

/* Single role header - no title, just buttons */
.profile-header.single-role-header {
  justify-content: flex-end;
  margin-bottom: var(--paddingStandard);
  padding-bottom: var(--paddingTight);
}

.profile-type-title {
  font-size: var(--subTitle);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.artist-title {
  color: #f8b19b;
}

.scientist-title {
  color: #6566be;
}

.profile-actions {
  display: flex;
  gap: var(--paddingStandard);
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: var(--paddingSpaceous);
}

.profile-content.compact {
  gap: var(--paddingStandard);
}

.profile-content.compact .status-card {
  padding: var(--paddingStandard);
}

.profile-content.compact h4 {
  font-size: var(--buttonText);
  margin: 0;
}

/* Artist-specific styling for dual layout */
.artist-section .completion-percentage,
.artist-percentage {
  color: #f8b19b !important;
}

.artist-section .progress-fill,
.artist-progress {
  background: #f8b19b !important;
}

.artist-section .action-btn,
.artist-btn {
  background: #f8b19b !important;
  color: white !important;
}

.artist-section .action-btn:hover,
.artist-btn:hover {
  opacity: 0.8;
}


.artist-section input:checked + .toggle-slider {
  background: #f8b19b !important;
}

/* Scientist-specific styling for dual layout */
.scientist-section .completion-percentage,
.scientist-percentage {
  color: #6566be !important;
}

.scientist-section .progress-fill,
.scientist-progress {
  background: #6566be !important;
}

.scientist-section .action-btn,
.scientist-btn {
  background: #6566be !important;
  color: white !important;
}

.scientist-section .action-btn:hover,
.scientist-btn:hover {
  opacity: 0.8;
}


.scientist-section input:checked + .toggle-slider {
  background: #6566be !important;
}


/* Profile Settings Toggles */
.profile-toggles {
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard);
}

.toggle-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.toggle-label {
  font-size: var(--labelText);
  color: var(--textColor);
  font-weight: 500;
}

/* Role-specific toggle styling */
.artist-toggle {
  background-color: #F6F5FA;
}

input:checked + .artist-toggle {
  background: #f8b19b !important;
}

.scientist-toggle {
  background-color: #F6F5FA;
}

input:checked + .scientist-toggle {
  background: #6566be !important;
}

/* Profile settings card styling */
.profile-settings {
  border-top: 1px solid var(--borderColor);
  margin-top: 0;
  padding-top: var(--paddingTight);
  background: rgba(246, 245, 250, 0.3);
  border-radius: 0 0 var(--stdRadius) var(--stdRadius);
}

.artist-settings {
  background: #FFFDFD;
}

.scientist-settings {
  background: #FEFEFF;
}

/* ===== RESPONSIVE ===== */

@media (max-width: 1200px) {
  .account-settings-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: var(--breakpointTablet)) {
  .dashboard-header {
    flex-direction: column;
    gap: var(--paddingStandard);
    text-align: center;
  }

  .tab-navigation {
    max-width: 100%;
  }

  .status-grid {
    grid-template-columns: 1fr;
  }

  .dual-profile-layout {
    grid-template-columns: 1fr;
    gap: var(--paddingStandard);
  }

  .profile-header {
    flex-direction: column;
    gap: var(--paddingStandard);
    text-align: center;
  }

  .settings-grid {
    grid-template-columns: 1fr;
  }

  .account-settings-grid {
    grid-template-columns: 1fr;
  }

  .welcome-title {
    font-size: 2rem;
  }
}
</style>